import { useState, useCallback } from 'react';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  Button,
  TextInput
} from '@etherisc/ui-kit';
import { 
  UploadIcon, 
  XIcon, 
  FileIcon,
  AlertCircleIcon,
  CheckCircleIcon
} from 'lucide-react';
import { shapefileService } from '../../services/climate/spatial/ShapefileService';
import type { Shapefile, ShapefileUploadData } from '../../types/shapefile';

interface ShapefileUploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (shapefile: Shapefile) => void;
  onError: (error: string) => void;
}

export function ShapefileUploadDialog({ 
  isOpen, 
  onClose, 
  onSuccess, 
  onError 
}: ShapefileUploadDialogProps) {
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Reset form when dialog opens/closes
  const resetForm = useCallback(() => {
    setName('');
    setFile(null);
    setUploading(false);
    setDragOver(false);
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !file) {
      onError('Please provide both a name and select a file');
      return;
    }

    setUploading(true);
    
    try {
      const uploadData: ShapefileUploadData = {
        name: name.trim(),
        file: file
      };
      
      const result = await shapefileService.uploadShapefile(uploadData);
      onSuccess(result);
      resetForm();
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
      
      // Handle different error types with user-friendly messages
      let errorMessage = 'Failed to upload shapefile';
      
      if (error instanceof Error) {
        // Check for specific error patterns
        if (error.message.includes('file_too_large') || error.message.includes('413') || error.message.includes('Payload Too Large')) {
          errorMessage = 'File is too large for the current server configuration. Please try a smaller file (under 1MB) or contact support for assistance with larger uploads.';
        } else if (error.message.includes('400')) {
          // Extract the actual validation message from 400 errors
          const match = error.message.match(/"message":"([^"]+)"/);
          if (match) {
            errorMessage = `Upload validation failed: ${match[1]}`;
          } else {
            errorMessage = 'The uploaded file does not contain valid shapefile data. Please ensure your ZIP file includes .shp, .shx, and .dbf files.';
          }
        } else if (error.message.includes('401') || error.message.includes('403')) {
          errorMessage = 'Authentication failed. Please refresh the page and try again.';
        } else if (error.message.includes('timeout') || error.message.includes('408')) {
          errorMessage = 'Upload timed out. Please check your connection and try again with a smaller file.';
        } else if (error.message.includes('502') || error.message.includes('503')) {
          errorMessage = 'The climate server is temporarily unavailable. Please try again in a few minutes.';
        } else {
          // Use the original error message for other cases
          errorMessage = error.message;
        }
      }
      
      onError(errorMessage);
    } finally {
      setUploading(false);
    }
  }, [name, file, onSuccess, onError, onClose, resetForm]);

  // Handle file selection
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      
      // Auto-suggest name based on filename if name is empty
      if (!name.trim()) {
        const baseName = selectedFile.name.replace(/\.zip$/i, '');
        setName(baseName);
      }
    }
  }, [name]);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.toLowerCase().endsWith('.zip')) {
      setFile(droppedFile);
      
      // Auto-suggest name based on filename if name is empty
      if (!name.trim()) {
        const baseName = droppedFile.name.replace(/\.zip$/i, '');
        setName(baseName);
      }
    }
  }, [name]);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-full max-w-md mx-4">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <UploadIcon className="h-5 w-5" />
                Upload Shapefile
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                disabled={uploading}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Input */}
              <div>
                <TextInput
                  label="Shapefile Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter a descriptive name for the shapefile"
                  disabled={uploading}
                  required
                />
              </div>

              {/* File Upload Area */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shapefile (ZIP format)
                </label>
                
                {/* Drop Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragOver 
                      ? 'border-blue-500 bg-blue-50' 
                      : file 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {file ? (
                    <div className="space-y-2">
                      <CheckCircleIcon className="h-8 w-8 text-green-600 mx-auto" />
                      <div className="flex items-center justify-center gap-2">
                        <FileIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">{file.name}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFile(null)}
                        disabled={uploading}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <UploadIcon className="h-8 w-8 text-gray-400 mx-auto" />
                      <div>
                        <p className="text-sm font-medium">
                          Drop your ZIP file here, or{' '}
                          <label className="text-blue-600 hover:text-blue-500 cursor-pointer">
                            browse
                            <input
                              type="file"
                              accept=".zip"
                              onChange={handleFileChange}
                              disabled={uploading}
                              className="hidden"
                            />
                          </label>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          ZIP files only, currently limited to ~1MB (server configuration)
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Requirements */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex gap-2">
                  <AlertCircleIcon className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Requirements:</p>
                    <ul className="text-xs space-y-1">
                      <li>• ZIP file containing shapefile components (.shp, .shx, .dbf)</li>
                      <li>• Current file size limit: ~1MB (temporary server restriction)</li>
                      <li>• Supported geometry types: Polygon, MultiPolygon, Point, LineString</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                  disabled={uploading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!name.trim() || !file || uploading}
                  className="flex-1"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <UploadIcon className="h-4 w-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 